import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type MemberOnboardingReview,
  type MemberOnboardingReviewEntry,
  useApplyMemberOnboardingMutation,
  useGetMemberOnboardingRunQuery,
  useReviewMemberOnboardingMutation,
} from '../api/memberOnboardingApi';

interface MemberOnboardingDialogProps {
  open: boolean;
  onClose: () => void;
}

const terminalStatuses = new Set(['completed', 'completed_with_errors', 'failed']);
const terminalBackfillStatuses = new Set(['not_started', 'completed', 'completed_with_errors']);

function statusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  if (status === 'ready' || status === 'completed') return 'success';
  if (status === 'duplicate' || status === 'processing') {
    return 'warning';
  }
  if (
    status === 'failed' ||
    status === 'invalid_email' ||
    status === 'unknown_person' ||
    status === 'not_session_first_coach'
  ) {
    return 'error';
  }
  return 'default';
}

function errorMessage(error: unknown, fallback: string): string {
  const data = (error as { data?: { message?: string; error_description?: string } })?.data;
  return data?.message || data?.error_description || fallback;
}

function ReviewTable({ review }: { review: MemberOnboardingReview }) {
  return (
    <TableContainer sx={{ maxHeight: 320, mt: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Line</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Member</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {review.entries.map((entry: MemberOnboardingReviewEntry) => (
            <TableRow key={`${entry.line}-${entry.input}`}>
              <TableCell>{entry.line}</TableCell>
              <TableCell>{entry.email || entry.input}</TableCell>
              <TableCell>{entry.name || '-'}</TableCell>
              <TableCell>
                <Chip size="small" label={entry.status} color={statusColor(entry.status)} />
              </TableCell>
              <TableCell>{entry.reason || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MemberOnboardingDialog({ open, onClose }: MemberOnboardingDialogProps) {
  const [emails, setEmails] = useState('');
  const [review, setReview] = useState<MemberOnboardingReview | undefined>();
  const [runId, setRunId] = useState<string>();
  const [publicationPending, setPublicationPending] = useState(false);
  const [isPollingRun, setIsPollingRun] = useState(false);
  const [localError, setLocalError] = useState('');
  const applyInFlightRef = useRef(false);
  const [reviewMemberOnboarding, reviewState] = useReviewMemberOnboardingMutation();
  const [applyMemberOnboarding, applyState] = useApplyMemberOnboardingMutation();
  const runQuery = useGetMemberOnboardingRunQuery(runId ?? '', {
    skip: !runId,
    pollingInterval: isPollingRun ? 3000 : 0,
  });

  useEffect(() => {
    if (
      runQuery.data &&
      terminalStatuses.has(runQuery.data.run.status) &&
      (!runQuery.data.run.backfill_status ||
        terminalBackfillStatuses.has(runQuery.data.run.backfill_status))
    ) {
      setIsPollingRun(false);
    }
  }, [runQuery.data]);

  const aggregateText = useMemo(() => {
    if (!runQuery.data) return '';
    const aggregate = runQuery.data.aggregate;
    return `${aggregate.completed_count ?? 0} completed, ${aggregate.processing_count ?? 0} processing, ${aggregate.queued_count ?? 0} queued, ${aggregate.failed_count ?? 0} failed; ${aggregate.generated_reflection_count ?? 0} generated, ${aggregate.suppressed_notification_count ?? 0} notifications suppressed`;
  }, [runQuery.data]);

  const reset = () => {
    if (applyInFlightRef.current || applyState.isLoading) return;
    setEmails('');
    setReview(undefined);
    setRunId(undefined);
    setPublicationPending(false);
    setIsPollingRun(false);
    setLocalError('');
    applyInFlightRef.current = false;
    onClose();
  };

  const handleReview = async () => {
    setLocalError('');
    setRunId(undefined);
    setIsPollingRun(false);
    try {
      setReview(await reviewMemberOnboarding({ emails }).unwrap());
    } catch (error) {
      setLocalError(errorMessage(error, 'Unable to review these email addresses.'));
    }
  };

  const handleApply = async () => {
    if (!review || applyInFlightRef.current) return;
    applyInFlightRef.current = true;
    setLocalError('');
    try {
      const result = await applyMemberOnboarding({
        emails,
        review_fingerprint: review.review_fingerprint,
      }).unwrap();
      setRunId(result.run.id);
      setPublicationPending(result.publication_pending === true);
      setIsPollingRun(true);
    } catch (error) {
      setLocalError(
        errorMessage(error, 'Unable to start member onboarding. Review again and retry.')
      );
    } finally {
      applyInFlightRef.current = false;
    }
  };

  const isBusy = reviewState.isLoading || applyState.isLoading;
  const runTerminal =
    !!runQuery.data &&
    terminalStatuses.has(runQuery.data.run.status) &&
    (!runQuery.data.run.backfill_status ||
      terminalBackfillStatuses.has(runQuery.data.run.backfill_status));

  return (
    <Dialog open={open} onClose={reset} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        Onboard members by email
        <IconButton
          aria-label="Close"
          onClick={reset}
          disabled={applyState.isLoading}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter one existing Moments member email per line. Review validates the batch before
          reflection jobs are changed. Invalid rows are skipped; valid rows can still be applied.
        </Typography>
        <TextField
          label="Member emails"
          value={emails}
          onChange={(event) => {
            setEmails(event.target.value);
            setReview(undefined);
            setLocalError('');
          }}
          multiline
          minRows={5}
          fullWidth
          disabled={!!runId || isBusy}
          placeholder="alice@example.com\nbob@example.com"
        />
        {localError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {localError}
          </Alert>
        )}

        {review && !runId && (
          <>
            <Alert severity={review.can_apply ? 'info' : 'warning'} sx={{ mt: 2 }}>
              {review.summary.ready} ready, {review.summary.blocking} blocking,{' '}
              {review.summary.duplicates} duplicate rows.
            </Alert>
            <ReviewTable review={review} />
          </>
        )}

        {runId && (
          <Box sx={{ mt: 2 }}>
            {runQuery.isLoading && <CircularProgress size={24} />}
            {runQuery.error && (
              <Alert severity="error">
                Unable to load onboarding progress. Retry polling later.
              </Alert>
            )}
            {publicationPending && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Queue publication is pending; the database recovery poller will start this run
                without reapplying the batch.
              </Alert>
            )}
            {runQuery.data && (
              <>
                <Alert
                  severity={
                    runTerminal
                      ? runQuery.data.run.status === 'completed' &&
                        runQuery.data.run.backfill_status !== 'completed_with_errors'
                        ? 'success'
                        : 'warning'
                      : 'info'
                  }
                >
                  Run <strong>{runQuery.data.run.status}</strong>; backfill{' '}
                  <strong>{runQuery.data.run.backfill_status || 'not_started'}</strong>:{' '}
                  {aggregateText}
                  {!runTerminal && ' — refreshing automatically.'}
                </Alert>
                <TableContainer sx={{ maxHeight: 300, mt: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reflections</TableCell>
                        <TableCell>Notifications</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runQuery.data.members.map((member) => (
                        <TableRow key={member.member_person_id}>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={member.status}
                              color={statusColor(member.status)}
                            />
                          </TableCell>
                          <TableCell>
                            {member.reused_reflection_count} reused / {member.enqueued_job_count}{' '}
                            queued / {member.generated_reflection_count} generated
                          </TableCell>
                          <TableCell>{member.suppressed_notification_count} suppressed</TableCell>
                          <TableCell>{member.error_message || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={reset} disabled={applyState.isLoading}>
          {runTerminal ? 'Done' : 'Cancel'}
        </Button>
        {!runId && (
          <Button variant="outlined" onClick={handleReview} disabled={!emails.trim() || isBusy}>
            {reviewState.isLoading ? (
              <CircularProgress size={18} />
            ) : review ? (
              'Review again'
            ) : (
              'Review'
            )}
          </Button>
        )}
        {!runId && review && (
          <Button variant="contained" onClick={handleApply} disabled={!review.can_apply || isBusy}>
            {applyState.isLoading ? <CircularProgress size={18} /> : 'Apply onboarding'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
